import {HideableValue, LocationCoordinates, OmitStrict, RichTextContent, Values} from '@wix/devzai-utils-common';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import type {DoppeBymoPageVideoSource} from './doppe-bymo-page-video-source';
import type {BymoPageActionMediaViewSettings} from '../bymo-pages';
import type {DoppeDtoContactMethod} from './doppe-dto-contact-method';
import type {
    DoppeActionTypeTestimonialsList
} from '../../doppe-action-types/doppe-action-type-testimonials-list/doppe-action-type-testimonials-list';
import type {
    DoppeActionTypeMediaGallery
} from '../../doppe-action-types/doppe-action-type-media-gallery/doppe-action-type-media-gallery';
import type {DoppeListActionSettings} from './doppe-list-action-settings';
import {
    BymoPagePropsContentSettingsMetadata,
    doppeListActionContentSettingsMetadataCreateForListActionCommonSettings
} from "../bymo-page-props-metadata/bymo-page-props-metadata";
import type {DoppeActionType} from "../doppe-action-types/doppe-action-type";
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    BymoPagePropsSpecialContentSettingMetadata
} from "../bymo-page-props-metadata/bymo-page-props-metadata";

/**
 * The types of content sections that can be used in any action that supports content sections.
 */
export const DoppeActionCommonContentSectionTypes = {
    RichContent: 'RichContent',
    Image: 'Image',
    Video: 'Video',
    Gallery: 'Gallery',
    Testimonials: 'Testimonials',
    Map: 'Map',
    Contact: 'Contact',
    Countdown: 'Countdown',
    CouponCodes: 'CouponCodes',
} as const;

export type DoppeActionCommonContentSectionTypes = Values<typeof DoppeActionCommonContentSectionTypes>;

/**
 * The types of content sections that can be used in specific actions that support content sections.
 */
export const DoppeActionTypeSpecificContentSectionTypes = {
    PropertyDetails: 'PropertyDetails',
} as const;

export type DoppeActionTypeSpecificContentSectionTypes = Values<typeof DoppeActionTypeSpecificContentSectionTypes>;

export const DoppeActionContentSectionType = {
    ...DoppeActionCommonContentSectionTypes,
    ...DoppeActionTypeSpecificContentSectionTypes,
} as const;

export type DoppeActionContentSectionType = Values<typeof DoppeActionContentSectionType>;


export type DoppeActionContentSection = DoppeActionContentSection.Section;

export type DoppeActionContentSectionContent =
    | DoppeActionContentSection.RichTextSettings
    | DoppeActionContentSection.ImageSettings
    | DoppeActionContentSection.VideoSettings
    | DoppeActionContentSection.GallerySettings
    | DoppeActionContentSection.TestimonialsSettings
    | DoppeActionContentSection.PropertyDetailsSettings
    | DoppeActionContentSection.MapSettings
    | DoppeActionContentSection.ContactSettings
    | DoppeActionContentSection.CountdownSettings
    | DoppeActionContentSection.CouponCodesSettings;


/**
 * A single coupon code entry inside a {@link DoppeActionContentSection.CouponCodesSettings}.
 * Modeled as an object (not a bare string) so future per-item fields can be added without
 * a data migration. The `label` is a `HideableValue` so each item can independently toggle
 * whether its label text shows above the code box in the viewer.
 */
export interface CouponCodeEntry {
    couponCode: string;
    label: HideableValue<string>;
}


export namespace DoppeActionContentSection {

    export type BaseType = {
        content: DoppeActionContentSectionContent;
    }

    export type Common = DoppeActionContentSection.BaseType & {
        isVisible: boolean;
    }

    export type Section = DoppeActionContentSection.Common & {
        layoutSettings: DoppeActionContentSectionLayoutSettings;
        title: string;
        isRequired: boolean;
        id: string;
    }

    export type Override = DoppeActionContentSection.Common & {
        useDefaultSectionContent: boolean;
    }

    export type ContentSectionWithOverride = DoppeActionContentSection.Section & DoppeActionContentSection.Override & {
        isDefaultSection: boolean;
    }


    type ContentBaseType<T extends DoppeActionContentSectionType> = {
        type: T;
    }

    export type RichTextSettings = ContentBaseType<typeof DoppeActionContentSectionType.RichContent> & {
        richText: RichTextContent
    };

    export type ImageSettings = ContentBaseType<typeof DoppeActionContentSectionType.Image> & {
        image: WixMediaResource | null;
        imageViewSettings: BymoPageActionMediaViewSettings;
    };

    export type VideoSettings = ContentBaseType<typeof DoppeActionContentSectionType.Video> & {
        video: DoppeBymoPageVideoSource | null;
        autoPlay: boolean;
    };

    export type GallerySettings = ContentBaseType<typeof DoppeActionContentSectionType.Gallery> &
        DoppeListActionSettings<DoppeActionTypeMediaGallery.MediaGalleryListItemSettings>  & {
    };

    export type TestimonialsSettings = ContentBaseType<typeof DoppeActionContentSectionType.Testimonials> &
        DoppeListActionSettings<DoppeActionTypeTestimonialsList.TestimonialItemSettings> & {

    }

    export type MapSettings = ContentBaseType<typeof DoppeActionContentSectionType.Map> & {
        address: {
            locationCoordinates: LocationCoordinates | null;
            displayAddress: HideableValue<string>;
        };
    };

    export type PropertyDetailsSettings = ContentBaseType<typeof DoppeActionContentSectionType.PropertyDetails> & {
        bedroomsNumber: HideableValue<number>;
        bathroomsNumber: HideableValue<number>;
        houseArea: HideableValue<string>;
        landArea: HideableValue<string>;
        floors: HideableValue<number>;
        yearBuilt: HideableValue<number>;
    };

    export type ContactSettings = ContentBaseType<typeof DoppeActionContentSectionType.Contact> & {
        contactMethods: DoppeDtoContactMethod[];
    };

    export type CouponCodesSettings = ContentBaseType<typeof DoppeActionContentSectionType.CouponCodes> & {
        couponCodes: CouponCodeEntry[];
    };

    export type CountdownSettings = ContentBaseType<typeof DoppeActionContentSectionType.Countdown> & {
        /**
         * Moment the countdown runs out — when the wall-clock date+time hits this point in
         * `timeZoneId`, the timer reads 00:00:00:00 and (if `confettiEnabled`) plays confetti.
         */
        endDateTime: {
            endDate: string;
            endTime: string;
            timeZoneId: string;
        };
        /**
         * One of the `DoppeActionTypeCountdownStyle` values, kept as a string so that this
         * file does not depend on the action-type folder.
         */
        timerStyle: string;
        /**
         * When true, the countdown unmounts after completion (after the confetti animation finishes).
         */
        hideWhenEnded: boolean;
    };
}

export const DoppeActionContentSectionsGeneralSettingsMetadata = {
    title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
    id: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    isRequired: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    isVisible: BymoPagePropsSpecialContentSettingMetadata.Ignored,
} satisfies BymoPagePropsContentSettingsMetadata<DoppeActionContentSection> | BymoPagePropsSpecialContentSettingMetadata;

export const DoppeActionContentSectionsSettingsMetadata = {
    [DoppeActionContentSectionType.RichContent]: {
        richText: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
    [DoppeActionContentSectionType.Image]: {
        image: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.SingleMedia, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
    [DoppeActionContentSectionType.Video]: {
        video: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.VideoSource, BymoPagePropsContentSettingSemanticMeaning.EntityVideoSource),
        autoPlay: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.autoPlay'}),
    },
    [DoppeActionContentSectionType.Gallery]: {
        ...doppeListActionContentSettingsMetadataCreateForListActionCommonSettings()
    },
    [DoppeActionContentSectionType.Testimonials]: {
        ...doppeListActionContentSettingsMetadataCreateForListActionCommonSettings()
    },
    [DoppeActionContentSectionType.Map]: {
    },
    [DoppeActionContentSectionType.PropertyDetails]: {
        bedroomsNumber: BymoPagePropsSpecialContentSettingMetadata.Ignored,
        bathroomsNumber: BymoPagePropsSpecialContentSettingMetadata.Ignored,
        houseArea: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
        landArea: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
        floors: BymoPagePropsSpecialContentSettingMetadata.Ignored,
        yearBuilt: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    },
    [DoppeActionContentSectionType.Contact]: {
        contactMethods: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.ContactMethods, BymoPagePropsContentSettingSemanticMeaning.EntityContactMethods),
    },
    [DoppeActionContentSectionType.Countdown]: {
        endDateTime: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
        timerStyle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
        hideWhenEnded: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
    [DoppeActionContentSectionType.CouponCodes]: {
        couponCodes: bymoPagePropsCreateContentSettingMetadata(
            BymoPagePropsContentSettingType.CouponCodes,
            BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified
        ),
    },
} satisfies {
    [SECTION in DoppeActionContentSectionContent as SECTION['type']]: BymoPagePropsContentSettingsMetadata<OmitStrict<SECTION, 'type'>> | BymoPagePropsSpecialContentSettingMetadata;
}

export const DoppeActionContentSectionsHideableSettingsNames: Partial<Record<DoppeActionContentSectionType, Record<string, true>>> = {
    [DoppeActionContentSectionType.PropertyDetails]: { houseArea: true, landArea: true },
};

/**
 * Metadata for content section types that use list-based settings (`DoppeListActionSettings<T>`).
 * Maps section type to its list item settings metadata — the same metadata shape used by action types.
 */
export const DoppeActionContentSectionsListItemSettingsMetadata = {
    [DoppeActionContentSectionType.Gallery]: {
        contentSettingsMetadata: {
            title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            media: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.SingleRequiredMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
            description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon)
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
            description: true,
            itemClickBehaviour: true,
            ribbon: true
        },
        bindableContentSettingsNames: {}
    },
    [DoppeActionContentSectionType.Testimonials]: {
        contentSettingsMetadata: {
            name: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            tagLine: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            profileMedia: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.SingleImage, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
            mainQuote: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon),
            date: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.DateTimeString, BymoPagePropsContentSettingSemanticMeaning.EntityPublishDate),
            rating: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Rating, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified)
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
            date: true,
            itemClickBehaviour: true,
            mainQuote: true,
            rating: true,
            ribbon: true,
            tagLine: true
        },
        bindableContentSettingsNames: {}
    },
} satisfies {
    [K in Extract<DoppeActionContentSectionContent, DoppeListActionSettings<any>> as K['type']]:
        K extends DoppeListActionSettings<infer ITEM>
            ? DoppeActionType.SettingsMetadata<OmitStrict<ITEM, 'id' | 'enabled' | 'deleted' | 'displayInSearch' | 'isDummy'>>
            : never
};

export const DoppeActionContentSectionLayoutType = {
    Inline: 'Inline',
    Collapsable: 'Collapsable',
} as const;

export type DoppeActionContentSectionLayoutType = Values<typeof DoppeActionContentSectionLayoutType>;

export type DoppeActionContentSectionLayoutSettings =
    | DoppeActionContentSectionLayoutSettings.Inline
    | DoppeActionContentSectionLayoutSettings.Collapsable;

export namespace DoppeActionContentSectionLayoutSettings {

    type BaseType<T extends DoppeActionContentSectionLayoutType> = {
        type: T;
    }

    export type Inline = BaseType<typeof DoppeActionContentSectionLayoutType.Inline> & {
        hideHeader: boolean;
    };

    export type Collapsable = BaseType<typeof DoppeActionContentSectionLayoutType.Collapsable> & {
        isOpenByDefault: boolean;
    };
}

